import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Plus, Send, Clock, Users, X } from "lucide-react";

const GAMES = ["Hell Let Loose"];
const ROLES = [
  "Officer", "Rifleman", "Assault", "Automatic Rifleman", "Medic", "Support",
  "Machine Gunner", "Anti-Tank", "Engineer", "Spotter", "Sniper",
  "Tank Commander", "Crewman", "Commander",
];

export default function Recruitment() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const [posts, setPosts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [myPlayers, setMyPlayers] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [postForm, setPostForm] = useState({ team_id: "", game: GAMES[0], role: ROLES[0], hours_per_week: 10, notes: "" });
  const [applyFor, setApplyFor] = useState(null);
  const [applyPlayer, setApplyPlayer] = useState("");
  const [applyNote, setApplyNote] = useState("");

  const load = async () => {
    const [p, t] = await Promise.all([api.get("/recruitment"), api.get("/teams")]);
    setPosts(p.data); setTeams(t.data);
    if (isAuthed) {
      const [mt, allP] = await Promise.all([api.get("/me/teams"), api.get("/free-agents")]);
      if (mt.data.length && !postForm.team_id) setPostForm((f) => ({ ...f, team_id: mt.data[0].id, game: mt.data[0].game }));
      setMyPlayers(allP.data.filter((p) => p.listed_by === user.id));
    }
  };
  useEffect(() => { load(); }, [isAuthed]); // eslint-disable-line

  const teamById = (id) => teams.find((t) => t.id === id);
  const myTeams = teams.filter((t) => t.owner_id === user?.id || (t.reps || []).includes(user?.id));

  const submitPost = async () => {
    if (!postForm.team_id) { toast.error("Pick a team first."); return; }
    try {
      await api.post("/recruitment", postForm);
      toast.success("Open slot posted.");
      setPostOpen(false);
      setPostForm({ team_id: myTeams[0]?.id || "", game: GAMES[0], role: ROLES[0], hours_per_week: 10, notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const closePost = async (id) => {
    try {
      await api.post(`/recruitment/${id}/close`);
      toast.success("Slot closed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitApply = async () => {
    if (!applyPlayer) { toast.error("Choose one of your listed players."); return; }
    try {
      await api.post(`/recruitment/${applyFor.id}/apply`, { player_id: applyPlayer, note: applyNote });
      toast.success(`Application sent to ${applyFor.team_name}.`);
      setApplyFor(null); setApplyPlayer(""); setApplyNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="recruitment-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#FF007F] font-mono tracking-[0.3em] text-xs mb-2">/ RECRUITMENT BOARD</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Open Slots</h1>
          <p className="text-gray-400 mt-2 max-w-xl text-sm">
            Teams post the seats they need to fill. Free agents apply with one tap using a listed profile.
          </p>
        </div>
        {isAuthed && myTeams.length > 0 && (
          <Dialog open={postOpen} onOpenChange={setPostOpen}>
            <DialogTrigger asChild>
              <Button data-testid="post-slot-btn"
                      className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold">
                <Plus className="w-4 h-4 mr-2" /> Post an open slot
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
              <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">New open slot</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Team</Label>
                  <Select value={postForm.team_id} onValueChange={(v) => {
                    const t = myTeams.find((x) => x.id === v);
                    setPostForm((f) => ({ ...f, team_id: v, game: t?.game || f.game }));
                  }}>
                    <SelectTrigger data-testid="post-team" className="rounded-none bg-[#05050A] border-[#232330] mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#101015] border-[#232330] text-white">
                      {myTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.tag}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Game</Label>
                    <Select value={postForm.game} onValueChange={(v) => setPostForm({ ...postForm, game: v })}>
                      <SelectTrigger className="rounded-none bg-[#05050A] border-[#232330] mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {GAMES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Role</Label>
                    <Select value={postForm.role} onValueChange={(v) => setPostForm({ ...postForm, role: v })}>
                      <SelectTrigger data-testid="post-role" className="rounded-none bg-[#05050A] border-[#232330] mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Hours/wk</Label>
                    <Input type="number" min={1} max={80} value={postForm.hours_per_week}
                           data-testid="post-hours"
                           onChange={(e) => setPostForm({ ...postForm, hours_per_week: Number(e.target.value) })}
                           className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Notes</Label>
                  <Textarea rows={3} value={postForm.notes} onChange={(e) => setPostForm({ ...postForm, notes: e.target.value })}
                            placeholder="Playstyle, timezones, scrim schedule…"
                            className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
                </div>
                <Button onClick={submitPost} data-testid="post-submit"
                        className="w-full rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold">
                  Publish slot
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.map((p) => {
          const isOwned = myTeams.some((t) => t.id === p.team_id);
          return (
            <div
              key={p.id}
              data-testid={`recruitment-${p.id}`}
              className="border border-[#232330] bg-[#101015] p-5 flex flex-col"
              style={{ borderLeft: `4px solid ${p.team_color}` }}
            >
              <div className="flex items-center justify-between text-[10px] font-mono tracking-widest uppercase mb-3">
                <span className="text-cyan-400">{p.game}</span>
                <span className="text-[#FF007F]">{p.role}</span>
              </div>
              <div className="font-heading text-xl font-black uppercase tracking-tight" style={{ color: p.team_color }}>
                {p.team_name}
              </div>
              <div className="text-xs text-gray-500 font-mono mt-1">{p.team_tag}</div>
              <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                <Clock className="w-3.5 h-3.5" />
                <span>~{p.hours_per_week} hrs / week</span>
              </div>
              {p.notes && <p className="text-xs text-gray-400 mt-3 line-clamp-3">{p.notes}</p>}
              <div className="mt-4 pt-4 border-t border-[#232330] flex items-center gap-2">
                {isAuthed && !isOwned && myPlayers.length > 0 && (
                  <Button
                    size="sm" onClick={() => { setApplyFor(p); setApplyPlayer(myPlayers[0].id); }}
                    data-testid={`apply-${p.id}`}
                    className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-[10px] font-bold flex-1"
                  >
                    <Send className="w-3 h-3 mr-2" /> Apply
                  </Button>
                )}
                {isAuthed && !isOwned && myPlayers.length === 0 && (
                  <Link to="/free-agents" className="flex-1 text-center text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-cyan-400 py-2 border border-[#232330]">
                    List a player to apply
                  </Link>
                )}
                {isOwned && (
                  <Button size="sm" variant="outline" onClick={() => closePost(p.id)}
                          data-testid={`close-${p.id}`}
                          className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-[10px] font-bold flex-1">
                    <X className="w-3 h-3 mr-2" /> Close slot
                  </Button>
                )}
                {!isAuthed && (
                  <Link
                    to="/login"
                    data-testid={`recruitment-login-${p.id}`}
                    className="flex-1 text-center text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 py-2 border border-cyan-400/40 hover:border-cyan-300"
                  >
                    <Send className="w-3 h-3 inline mr-2" /> Login to apply
                  </Link>
                )}
              </div>
            </div>
          );
        })}
        {posts.length === 0 && (
          <div className="col-span-full border border-[#232330] p-10 text-center text-gray-500">
            <Megaphone className="w-10 h-10 mx-auto text-gray-600 mb-3" />
            No open slots yet. Managers — post the seats you need to fill.
          </div>
        )}
      </div>

      <Dialog open={!!applyFor} onOpenChange={(o) => !o && setApplyFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Apply · {applyFor?.team_name} · {applyFor?.role}
            </DialogTitle>
          </DialogHeader>
          <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Which listed player?</Label>
          <Select value={applyPlayer} onValueChange={setApplyPlayer}>
            <SelectTrigger data-testid="apply-player-select" className="rounded-none bg-[#05050A] border-[#232330]">
              <SelectValue placeholder="Pick one of your free agents" />
            </SelectTrigger>
            <SelectContent className="bg-[#101015] border-[#232330] text-white">
              {myPlayers.map((p) => <SelectItem key={p.id} value={p.id}>{p.handle} · {p.role}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Message (optional)</Label>
          <Textarea rows={3} value={applyNote} onChange={(e) => setApplyNote(e.target.value)}
                    className="rounded-none bg-[#05050A] border-[#232330]" />
          <Button onClick={submitApply} data-testid="confirm-apply"
                  className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold">
            Send application
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
